import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trophy, Send, Loader2, Link2, Settings2, ListPlus, Check, Bell, Wand2, Trash2, KeyRound } from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ListRow, RowAction, Section, EmptyRow } from "../components/mobile/ListRow";
import { useT, locale } from "../lib/i18n";

const TABS = [
  { k: "report", l: "Weekrapport", icon: Trophy },
  { k: "matches", l: "Wedstrijden", icon: ListPlus },
  { k: "mapping", l: "Koppelingen", icon: Link2 },
  { k: "settings", l: "Instellingen", icon: Settings2 },
];

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-slate-300"}`}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function ReportTab({ report, offset, setOffset, onSend, sending, sendResult }) {
  const t = useT();
  if (!report) return <div className="px-4 py-6 text-sm text-slate-400">{t("Laden…")}</div>;
  return (
    <div data-testid="matches-report">
      <div className="flex items-center gap-2 px-4 pt-4 pb-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          {[[-1, t("Vorige week")], [0, t("Deze week")]].map(([o, l]) => (
            <button key={o} onClick={() => setOffset(o)} className={`h-8 px-3 text-xs font-bold ${offset === o ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid={`matches-report-offset-${o === 0 ? "0" : "prev"}`}>{l}</button>
          ))}
        </div>
        <span className="text-xs text-slate-500" data-testid="matches-report-range">{t("Week")} {report.iso_week} · {report.week_start} {t("t/m")} {report.week_end}</span>
        <button onClick={onSend} disabled={sending} className="ml-auto pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="matches-report-send">
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {t("Nu versturen")}
        </button>
      </div>
      {sendResult && (
        <div className="mx-4 mb-2 text-xs rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="matches-report-send-result">
          {Object.entries(sendResult.channels || {}).map(([ch, r]) => <span key={ch} className="mr-3">{ch}: <b className={r.ok ? "text-emerald-700" : "text-red-600"}>{r.ok ? t("verzonden") : r.error}</b></span>)}
        </div>
      )}
      <Section title={t("Gespeeld")} count={report.played.length} testid="matches-report-played">
        {report.played.length === 0 && <EmptyRow testid="matches-report-empty">{t("Geen clients met speelminuten deze week.")}</EmptyRow>}
        {report.played.map((p) => p.matches.map((m) => (
          <ListRow key={m.id} tone={m.goals + m.assists > 0 ? "green" : "none"} leading={new Date(m.date).toLocaleDateString(locale(), { weekday: "short", day: "numeric" })}
            title={`${p.full_name} · vs. ${m.opponent}`} meta={`${m.minutes} min — ${m.goals} goals — ${m.assists} assists${m.competition ? ` · ${m.competition}` : ""}${m.result ? ` · ${m.result}` : ""}`}
            testid={`matches-report-row-${m.id}`} />
        )))}
      </Section>
      {report.no_minutes.length > 0 && (
        <Section title={t("Geen minuten")} count={report.no_minutes.length} testid="matches-report-no-minutes">
          {report.no_minutes.map((p) => <ListRow key={p.id} tone="orange" title={p.full_name} meta={p.matches.map((m) => `vs. ${m.opponent}`).join(" · ")} testid={`matches-report-nomin-${p.id}`} />)}
        </Section>
      )}
      {report.did_not_play.length > 0 && (
        <Section title={t("Niet gespeeld deze week")} count={report.did_not_play.length} testid="matches-report-dnp">
          <div className="px-4 py-3 text-sm text-slate-600 leading-relaxed">{report.did_not_play.map((c) => c.full_name).join(", ")}</div>
        </Section>
      )}
    </div>
  );
}

function MatchesTab({ stats, clients, onChanged }) {
  const t = useT();
  const [form, setForm] = useState({ client_id: "", date: new Date().toISOString().slice(0, 10), opponent: "", competition: "", minutes: 90, goals: 0, assists: 0, result: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.client_id || !form.opponent) { setErr(t("Kies een client en vul de tegenstander in.")); return; }
    setBusy(true); setErr("");
    try { await api.post("/matches/stats", { ...form, minutes: Number(form.minutes), goals: Number(form.goals), assists: Number(form.assists), competition: form.competition || null, result: form.result || null }); setForm((f) => ({ ...f, opponent: "", goals: 0, assists: 0, result: "" })); onChanged(); }
    catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="matches-tab">
      <div className="mx-4 mt-4 rounded-xl border border-slate-200 p-4" data-testid="matches-manual-form">
        <div className="text-sm font-semibold text-slate-800 mb-3">{t("Handmatig invoeren")} <span className="text-xs font-normal text-slate-500">— {t("voor competities zonder datafeed")}</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <select className="pm-input col-span-2" value={form.client_id} onChange={(e) => set("client_id", e.target.value)} data-testid="manual-client">
            <option value="">{t("— client —")}</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <input type="date" className="pm-input" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="manual-date" />
          <input className="pm-input" placeholder={t("Uitslag 2–1")} value={form.result} onChange={(e) => set("result", e.target.value)} data-testid="manual-result" />
          <input className="pm-input col-span-2" placeholder={t("Tegenstander")} value={form.opponent} onChange={(e) => set("opponent", e.target.value)} data-testid="manual-opponent" />
          <input className="pm-input col-span-2" placeholder={t("Competitie")} value={form.competition} onChange={(e) => set("competition", e.target.value)} data-testid="manual-competition" />
          {[["minutes", "Min"], ["goals", "Goals"], ["assists", "Assists"]].map(([k, l]) => (
            <label key={k} className="text-xs text-slate-500">{l}<input type="number" min="0" className="pm-input mt-1 w-full" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`manual-${k}`} /></label>
          ))}
          <div className="flex items-end"><RowAction primary onClick={save} testid="manual-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t("Opslaan")}</RowAction></div>
        </div>
        {err && <div className="text-xs text-red-600 mt-2" data-testid="manual-error">{err}</div>}
      </div>
      <Section title={t("Recente wedstrijden")} count={stats.length} testid="matches-list">
        {stats.length === 0 && <EmptyRow>{t("Nog geen wedstrijddata.")}</EmptyRow>}
        {stats.map((m) => (
          <ListRow key={m.id} tone={m.minutes > 0 ? "green" : "slate"} leading={new Date(m.date).toLocaleDateString(locale(), { day: "numeric", month: "short" })}
            title={`${m.client_name || "?"} · vs. ${m.opponent}`} meta={`${m.minutes} min — ${m.goals} goals — ${m.assists} assists${m.competition ? ` · ${m.competition}` : ""} · ${m.source}`}
            testid={`match-row-${m.id}`}
            actions={m.source !== "api_football" ? <RowAction onClick={async () => { await api.delete(`/matches/stats/${m.id}`); onChanged(); }} testid={`match-delete-${m.id}`} title={t("Verwijderen")}><Trash2 size={14} /></RowAction> : null} />
        ))}
      </Section>
    </div>
  );
}

function MappingTab({ mapping, provider, onChanged }) {
  const t = useT();
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState("");
  const [manual, setManual] = useState({});
  const auto = async (c) => {
    setBusyId(c.id); setMsg("");
    try { const { data } = await api.post(`/matches/mapping/${c.id}/auto`); setMsg(data.ok ? t("{a} gekoppeld aan {b} ({c})", { a: c.full_name, b: data.matched.name, c: data.matched.team }) : data.message || data.error); onChanged(); }
    finally { setBusyId(null); }
  };
  const saveManual = async (c) => {
    const v = manual[c.id] || {};
    await api.put(`/matches/mapping/${c.id}`, { af_player_id: v.player ? Number(v.player) : null, af_team_id: v.team ? Number(v.team) : c.af_team_id || null });
    onChanged();
  };
  return (
    <div data-testid="matches-mapping">
      <div className="px-4 pt-4 text-xs text-slate-500">
        Provider: <b data-testid="mapping-provider-status">{provider?.status === "live" ? "API-Football live" : provider?.configured ? `API-Football — ${provider.status}` : t("geen sleutel")}</b>. {t("Niet gekoppelde clients kun je handmatig koppelen (speler-ID) of stats handmatig invoeren.")}
      </div>
      {msg && <div className="mx-4 mt-2 text-xs text-emerald-700" data-testid="mapping-msg">{msg}</div>}
      <Section title="Clients" count={mapping.length} testid="mapping-list">
        {mapping.map((c) => (
          <ListRow key={c.id} tone={c.mapped ? "green" : "orange"} title={c.full_name}
            meta={`${c.current_club || "—"}${c.mapped ? ` · team ${c.af_team_id} · ${t("speler")} ${c.af_player_id}` : c.af_team_id ? ` · team ${c.af_team_id} · ${t("speler-ID ontbreekt")}` : ` · ${t("niet gekoppeld")}`}${c.last_match ? ` · ${t("laatste")}: vs ${c.last_match.opponent} (${c.last_match.source})` : ""}`}
            testid={`mapping-row-${c.id}`}
            actions={<>
              <input className="pm-input h-9 w-24 text-xs" placeholder={t("speler-ID")} value={manual[c.id]?.player ?? ""} onChange={(e) => setManual((m) => ({ ...m, [c.id]: { ...(m[c.id] || {}), player: e.target.value } }))} data-testid={`mapping-player-input-${c.id}`} />
              <RowAction onClick={() => saveManual(c)} testid={`mapping-save-${c.id}`} title={t("Handmatig koppelen")}><Check size={14} /></RowAction>
              <RowAction onClick={() => auto(c)} testid={`mapping-auto-${c.id}`} title={t("Automatisch zoeken")}>{busyId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}</RowAction>
            </>} />
        ))}
      </Section>
    </div>
  );
}

function SettingsTab({ settings, provider, isAdmin, onSaved, onProviderSaved }) {
  const t = useT();
  const [form, setForm] = useState(settings);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [keyMsg, setKeyMsg] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => { setSaving(true); try { const { data } = await api.put("/matches/settings", form); setForm(data); onSaved(data); } finally { setSaving(false); } };
  const saveKey = async () => { try { const { data } = await api.post("/matches/provider-key", { api_key: key }); setKeyMsg(data.ok ? t("Sleutel actief ({x})", { x: data.label }) : t("Sleutel opgeslagen maar provider zegt: {x}", { x: data.status })); setKey(""); onProviderSaved(); } catch (e) { setKeyMsg(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const testNotif = async () => { try { const { data } = await api.post("/matches/test-notification"); setTestMsg(t("Testmelding verstuurd: {x}", { x: `${data.match.client} vs ${data.match.opponent}` })); } catch (e) { setTestMsg(formatApiErrorDetail(e?.response?.data?.detail)); } };
  return (
    <div className="px-4 pt-4 space-y-6 max-w-2xl" data-testid="matches-settings">
      <div className="space-y-3">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Bell size={14} /> {t("Wedstrijdmeldingen")}</div>
        <label className="block text-xs text-slate-600">{t("Moment")}
          <select className="pm-input mt-1 w-full" value={form.notify_trigger} onChange={(e) => set("notify_trigger", e.target.value)} data-testid="matches-set-trigger">
            <option value="full_time">{t("Bij einde wedstrijd")}</option><option value="kickoff">{t("Bij aftrap")}</option><option value="both">{t("Aftrap én einde")}</option>
          </select>
        </label>
        <div className="flex items-center justify-between text-sm"><span>{t("In-app melding")}</span><Toggle checked={form.notify_in_app} onChange={(v) => set("notify_in_app", v)} testid="matches-set-inapp" /></div>
        <div className="flex items-center justify-between text-sm"><span>{t("WhatsApp-push (Twilio, nummer uit briefing-instellingen)")}</span><Toggle checked={form.notify_whatsapp} onChange={(v) => set("notify_whatsapp", v)} testid="matches-set-whatsapp" /></div>
        <RowAction onClick={testNotif} testid="matches-test-notification"><Bell size={14} /> {t("Testmelding")}</RowAction>
        {testMsg && <div className="text-xs text-emerald-700" data-testid="matches-test-result">{testMsg}</div>}
      </div>
      <div className="space-y-3">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Trophy size={14} /> {t("Weekrapport")}</div>
        <div className="flex items-center justify-between text-sm"><span>{t("Automatisch versturen (kanalen uit briefing-instellingen)")}</span><Toggle checked={form.weekly_enabled} onChange={(v) => set("weekly_enabled", v)} testid="matches-set-weekly" /></div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs text-slate-600">{t("Dag")}
            <select className="pm-input mt-1 w-full" value={form.weekly_day} onChange={(e) => set("weekly_day", Number(e.target.value))} data-testid="matches-set-day">
              {["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"].map((d, i) => <option key={d} value={i}>{t(d)}</option>)}
            </select>
          </label>
          <label className="block text-xs text-slate-600">{t("Tijd")}<input type="time" className="pm-input mt-1 w-full" value={form.weekly_time} onChange={(e) => set("weekly_time", e.target.value)} data-testid="matches-set-time" /></label>
        </div>
        <div className="flex items-center justify-between text-sm"><span>{t("Niet-gespeelde clients tonen")}</span><Toggle checked={form.include_non_playing} onChange={(v) => set("include_non_playing", v)} testid="matches-set-nonplaying" /></div>
        <RowAction primary onClick={save} testid="matches-settings-save">{saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t("Instellingen opslaan")}</RowAction>
      </div>
      {isAdmin && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><KeyRound size={14} /> {t("API-Football sleutel")}</div>
          <div className="text-xs text-slate-500">Status: <b data-testid="matches-provider-status">{provider?.status || t("geen sleutel")}</b>{provider?.label ? ` · ${provider.label}` : ""}</div>
          <div className="flex gap-2">
            <input className="pm-input flex-1" type="password" placeholder={t("Plak nieuwe x-apisports-key")} value={key} onChange={(e) => setKey(e.target.value)} data-testid="matches-provider-key-input" />
            <RowAction onClick={saveKey} testid="matches-provider-key-save"><Check size={14} /> {t("Opslaan")}</RowAction>
          </div>
          {keyMsg && <div className="text-xs text-slate-600" data-testid="matches-provider-key-msg">{keyMsg}</div>}
        </div>
      )}
    </div>
  );
}

export default function MatchesPage() {
  const t = useT();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "report";
  const setTab = (t) => { const n = new URLSearchParams(params); n.set("tab", t); setParams(n, { replace: true }); };
  const [offset, setOffset] = useState(-1);
  const [report, setReport] = useState(null);
  const [stats, setStats] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [provider, setProvider] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const loadReport = useCallback(() => api.get(`/matches/report?offset=${offset}`).then((r) => setReport(r.data)), [offset]);
  const loadAll = useCallback(async () => {
    const [s, m, c, st, p] = await Promise.all([api.get("/matches/stats"), api.get("/matches/mapping"), api.get("/clients"), api.get("/matches/settings"), api.get("/matches/provider")]);
    setStats(s.data || []); setMapping(m.data || []); setClients(c.data || []); setSettings(st.data); setProvider(p.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const send = async () => { setSending(true); try { const { data } = await api.post(`/matches/report/send-now?offset=${offset}`); setSendResult(data); } finally { setSending(false); } };
  const refresh = () => { loadAll(); loadReport(); };

  return (
    <Layout title={t("Wedstrijden")} subtitle={t("Meldingen · weekoverzicht prestaties")} compact mobileFlush>
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto whitespace-nowrap px-2 lg:px-0" data-testid="matches-tabs">
        {TABS.map((tb) => (
          <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`matches-tab-${tb.k}`}
            className={`px-3 h-11 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px ${tab === tb.k ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500"}`}>
            <tb.icon size={13} /> {t(tb.l)}
          </button>
        ))}
      </div>
      <div className="max-w-3xl">
        {tab === "report" && <ReportTab report={report} offset={offset} setOffset={setOffset} onSend={send} sending={sending} sendResult={sendResult} />}
        {tab === "matches" && <MatchesTab stats={stats} clients={clients} onChanged={refresh} />}
        {tab === "mapping" && <MappingTab mapping={mapping} provider={provider} onChanged={refresh} />}
        {tab === "settings" && settings && <SettingsTab settings={settings} provider={provider} isAdmin={user?.role === "admin"} onSaved={setSettings} onProviderSaved={() => api.get("/matches/provider").then((r) => setProvider(r.data))} />}
      </div>
    </Layout>
  );
}
